import { Search, X } from 'lucide-react';
import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '../lib/cn.js';

export interface SearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> {
  value: string;
  onValueChange: (value: string) => void;
  /** Mono count of matches shown on the right, e.g. `12 / 4,218`. */
  count?: string;
  /** Compact height for toolbars. */
  compact?: boolean;
}

/** Real search input (⌘F targets): magnifier, clear button, optional mono match count. */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { value, onValueChange, count, compact = false, className, placeholder = 'Search', 'aria-label': ariaLabel, ...rest },
  ref,
) {
  // Room for the right cluster, in px rather than on the spacing scale: the count and the clear
  // button are px-sized, so density must not change what fits. ~7px per mono character at 11px.
  const paddingRight = Math.max(12, (count ? count.length * 7 + 12 : 0) + (value ? 24 : 0) + 8);
  return (
    <span className={cn('relative inline-flex w-full items-center', className)}>
      <Search size={12} aria-hidden className="pointer-events-none absolute left-2.5 text-mute" />
      <input
        ref={ref}
        type="search"
        role="searchbox"
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel ?? placeholder}
        className={cn(
          'w-full appearance-none rounded-sm border border-line bg-bg pl-7 text-nav text-ink placeholder:text-mute transition-colors hover:border-mute/60 focus:border-brand focus:outline-none [&::-webkit-search-cancel-button]:appearance-none',
          compact ? 'py-1 text-xs' : 'py-1.5',
        )}
        style={{ paddingRight }}
        {...rest}
      />
      <span className="absolute right-2 flex items-center gap-2">
        {count ? <span className="font-mono text-2xs tabular-nums text-mute">{count}</span> : null}
        {value ? (
          <button type="button" aria-label="Clear search" onClick={() => onValueChange('')} className="inline-flex h-4 w-4 items-center justify-center rounded-sm text-mute transition-colors hover:text-ink">
            <X size={11} aria-hidden />
          </button>
        ) : null}
      </span>
    </span>
  );
});
